import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'abga_pwa_install_dismissed';

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [isIos, setIsIos] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    setIsIos(isIosDevice());
    setIsInstalled(isStandalone());

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    const onDisplayMode = () => setIsInstalled(isStandalone());

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', onDisplayMode);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener('change', onDisplayMode);
    };
  }, []);

  const canNativeInstall = Boolean(deferredPrompt);
  const canShowIosHint = isIos && !isInstalled && !dismissed;
  const shouldShowPrompt =
    !isInstalled &&
    !dismissed &&
    (canNativeInstall || canShowIosHint);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') {
      setIsInstalled(true);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const openIosHelp = useCallback(() => setShowIosHelp(true), []);
  const closeIosHelp = useCallback(() => setShowIosHelp(false), []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
    setShowIosHelp(false);
  }, []);

  return {
    shouldShowPrompt,
    canNativeInstall,
    canShowIosHint,
    isInstalled,
    isIos,
    showIosHelp,
    install,
    openIosHelp,
    closeIosHelp,
    dismiss,
  };
}
